import { Alert, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { PublishingBotPermissions } from '../../../api/src/telegram/publishing.types';
import { api } from '../api/client';
import { Stack, Typography } from '../components/mui-system';

type CheckState =
  | { status: 'loading' }
  | { status: 'success'; result: PublishingBotPermissions }
  | { status: 'error'; message: string };

// The form keys this component by workspace, bot revision and destination. Remounting
// discards both the visible result and pending responses when any of them changes.
export function TelegramBotPermissions({
  workspaceId,
  botId,
  chatId,
  disabled,
}: {
  workspaceId: string;
  botId: string;
  chatId: string;
  disabled: boolean;
}) {
  const [state, setState] = useState<CheckState | null>(null);
  const requestId = useRef(0);
  useEffect(
    () => () => {
      requestId.current++;
    },
    [],
  );
  async function check() {
    const request = ++requestId.current;
    setState({ status: 'loading' });
    try {
      const result = await api.publishingCheckBotPermissions(workspaceId, botId, chatId);
      if (request === requestId.current) setState({ status: 'success', result });
    } catch (e) {
      if (request === requestId.current)
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : 'Не удалось проверить права бота',
        });
    }
  }
  return (
    <Stack gap={1}>
      <Button
        type="button"
        variant="outlined"
        disabled={disabled || !botId || !chatId || state?.status === 'loading'}
        onClick={() => void check()}
      >
        {state?.status === 'loading' ? 'Проверяем права…' : 'Проверить права бота'}
      </Button>
      <Typography variant="caption" color="text.secondary">
        Проверка не отправляет сообщения и не сохраняет форму.
      </Typography>
      {state?.status === 'loading' && (
        <Typography role="status">Проверяем права в Telegram…</Typography>
      )}
      {state?.status === 'success' && (
        <Alert severity="success" role="status">
          Права подтверждены. Бот — администратор канала {state.result.chatId} с правом публикации
          сообщений. Проверено: {new Date(state.result.checkedAt).toLocaleString('ru-RU')}.
        </Alert>
      )}
      {state?.status === 'error' && <Alert severity="error">{state.message}</Alert>}
    </Stack>
  );
}
